import {Component, forwardRef, Input} from '@angular/core';
import {CacheDataService} from '../../api/services/cache-data.service';
import {FlatTreeControl} from '@angular/cdk/tree';
import {Occupation} from '../../api/models/occupation';
import {TreeViewSelectionNode} from './helpers/tree-view-selection-node';
import {TreeViewDataSource} from './helpers/tree-vew-data-source';
import {FilterEditorComponent} from './filter-editor/filter-editor.component';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {OccupationKey} from '../../api/models/occupation-key';

@Component({
  selector: "occupation-filter-editor",
  templateUrl: "./filter-editor/filter-editor.component.html",
  styleUrls: ["./filter-editor/filter-editor.component.css"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OccupationFilterEditorComponent),
      multi: true
    }
  ]
})
export class OccupationFilterEditorComponent extends FilterEditorComponent<Occupation, OccupationKey, TVSNOccupation, OccupationDataSource> implements ControlValueAccessor {

  constructor(cacheDataService: CacheDataService) {
    super(cacheDataService);
  }

  protected getNewDataSource(treeControl: FlatTreeControl<TVSNOccupation, TVSNOccupation>, cacheDataService: CacheDataService): OccupationDataSource {
    return new OccupationDataSource(treeControl, cacheDataService);
  }
}

export class OccupationDataSource extends TreeViewDataSource<Occupation, OccupationKey, TVSNOccupation> {

  constructor(
    treeControl: FlatTreeControl<TVSNOccupation>,
    public cacheDataService: CacheDataService) {
    super(treeControl);
  }

  protected loadInitialData(callback: (results: TVSNOccupation[]) => void) {
    this.cacheDataService.cacheDataGetOccupationMajorGroups({ pageNumber: 1, pageSize: 1000 }).subscribe(result => {
      const nodes = result.page.map(item => new TVSNOccupation(
        [], this.blankCover(item.majorGroup), 1,
        {
          majorGroupId: item.majorGroupId,
          majorGroup: item.majorGroup,
          minorGroupId: undefined,
          minorGroup: undefined,
          broadGroupId: undefined,
          broadGroup: undefined,
          socCode: undefined,
          detailedOccupation: undefined,
          onetCode: undefined,
          onetOccupation: undefined,
          description: undefined
        },
        undefined, true, true
      ));
      callback(nodes);
    });
  }

  protected loadChildNodes(node: TVSNOccupation, callback: (results: TVSNOccupation[]) => void) {
    if (node.level === 1) {
      this.cacheDataService.cacheDataGetOccupationMinorGroups({ majorGroupId: node.sourceValue.majorGroupId, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes : TVSNOccupation[] = result.page.map(item => new TVSNOccupation(
          [], this.blankCover(item.minorGroup), node.level + 1,
          {
            majorGroupId: item.majorGroupId,
            majorGroup: item.majorGroup,
            minorGroupId: item.minorGroupId,
            minorGroup: item.minorGroup,
            broadGroupId: undefined,
            broadGroup: undefined,
            socCode: undefined,
            detailedOccupation: undefined,
            onetCode: undefined,
            onetOccupation: undefined,
            description: undefined
          },
          node, true, true
        ));
        callback(nodes);
      });
    } else if (node.level === 2) {
      this.cacheDataService.cacheDataGetOccupationBroadGroups({ minorGroupId: node.sourceValue.minorGroupId, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes: TVSNOccupation[] = result.page.map(item => new TVSNOccupation(
          [], this.blankCover(item.broadGroup), node.level + 1,
          {
            majorGroupId: item.majorGroupId,
            majorGroup: item.majorGroup,
            minorGroupId: item.minorGroupId,
            minorGroup: item.minorGroup,
            broadGroupId: item.broadGroupId,
            broadGroup: item.broadGroup,
            socCode: undefined,
            detailedOccupation: undefined,
            onetCode: undefined,
            onetOccupation: undefined,
            description: undefined
          },
          node, true, true
        ));
        callback(nodes);
      });
    } else if (node.level === 3) {
      this.cacheDataService.cacheDataGetOccupationSocCodes({ majorGroupId: node.sourceKey.majorGroupId, minorGroupId: node.sourceKey.minorGroupId,  broadGroupId: node.sourceKey.broadGroupId, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes: TVSNOccupation[] = result.page.map(item => new TVSNOccupation(
          [], this.blankCover(item.onetOccupation), node.level + 1,
          {
            majorGroupId: item.majorGroupId,
            majorGroup: item.majorGroup,
            minorGroupId: item.minorGroupId,
            minorGroup: item.minorGroup,
            broadGroupId: item.broadGroupId,
            broadGroup: item.broadGroup,
            socCode: item.socCode,
            detailedOccupation: item.detailedOccupation,
            onetCode: item.onetCode,
            onetOccupation: item.onetOccupation,
            description: item.description
          },
          node, false, true
        ));
        callback(nodes);
      });
    }
  }

}

export class TVSNOccupation extends TreeViewSelectionNode<Occupation, OccupationKey> {

  protected deriveKey(): OccupationKey {
    return {
      majorGroupId: this.sourceValue.majorGroupId,
      minorGroupId: this.sourceValue.minorGroupId,
      broadGroupId: this.sourceValue.broadGroupId,
      onetCode: this.sourceValue.onetCode
    };
  }

  protected isAnyKnownChildSelected(allKnownSelections: OccupationKey[]): boolean {
    // Rules:
    // If OnetCode != null, there can be no children
    // If current level matches, it must NOT match entirely (i.e, there target should have a lower level than THIS node

    if (!this.sourceValue.onetCode) {
      if (this.sourceValue.broadGroupId) {
        return allKnownSelections?.some(x => x.broadGroupId == this.sourceValue.broadGroupId && x.onetCode);
      } else if (this.sourceValue.minorGroupId) {
        return allKnownSelections?.some(x => x.minorGroupId === this.sourceValue.minorGroupId && x.broadGroupId);
      } else if (this.sourceValue.majorGroupId) {
        return allKnownSelections?.some(x => x.majorGroupId === this.sourceValue.majorGroupId && x.minorGroupId);
      }
    }

    return false;
  }

  public isKeyForMe(key: OccupationKey): boolean {
    return key.onetCode === this.sourceValue.onetCode
    && key.broadGroupId == this.sourceValue.broadGroupId
    && key.minorGroupId === this.sourceValue.minorGroupId
    && key.majorGroupId === this.sourceValue.majorGroupId;
  }

  public isChildOf(key: OccupationKey): boolean {
    if (key.broadGroupId != null) {
      return key.broadGroupId === this.sourceValue.broadGroupId
      && key.minorGroupId === this.sourceValue.minorGroupId
      && key.majorGroupId === this.sourceValue.majorGroupId;
    } else if (key.minorGroupId != null) {
      return key.minorGroupId === this.sourceValue.minorGroupId
        && key.majorGroupId === this.sourceValue.majorGroupId;
    } else if (key.majorGroupId != null) {
      return key.majorGroupId === this.sourceValue.majorGroupId;
    }

    return false;
  }

  public toSourceKey(): OccupationKey {
    return {
      majorGroupId: this.sourceValue.majorGroupId,
      minorGroupId: this.sourceValue.minorGroupId,
      broadGroupId: this.sourceValue.broadGroupId,
      onetCode: this.sourceValue.onetCode
    }
  }

  public matchesKey(key: OccupationKey): boolean {
    return key.onetCode === this.sourceValue.onetCode
    && key.broadGroupId == this.sourceValue.broadGroupId
    && key.minorGroupId === this.sourceValue.minorGroupId
    && key.majorGroupId === this.sourceValue.majorGroupId;
  }

}
